import { useQuery, type UseQueryOptions } from '@tanstack/react-query';
import { z } from 'zod';

import { ENDPOINT } from '~/constants/endpoints';

import { type UUID } from '~/types/common';

import { vestigasApi } from '~/services/kyClient';

import { log } from '~/utils';

import { queryKeysCompany } from './queryKeys';

const zodCompanyInvoiceCheckDelaySchema = z.object({
  delayedDays: z.number().nonnegative().nullable().prefault(0),
  onlyIncludeWorkdays: z.boolean().nullable().prefault(true),
});

export type CompanyInvoiceCheckDelay = z.infer<
  typeof zodCompanyInvoiceCheckDelaySchema
>;

/**
 * Fetches the company invoice check delay from the API.
 *
 * @param companyId - The ID of the company to fetch.
 * @returns The company invoice check delay.
 * @throws Error if the API request fails.
 *
 * @see https://app.dev.vestigas.com/redoc#tag/VESTIGAS-Admin-Company/operation/get_invoice_check_delay_vadmin_company__company_id__invoice_check_delay_get
 */
export const fetchCompanyInvoiceCheckDelay = async (companyId: UUID) => {
  try {
    return await vestigasApi
      .get(ENDPOINT.COMPANY.INVOICE_CHECK_DELAY.GET(companyId), {
        validate: { response: zodCompanyInvoiceCheckDelaySchema },
      })
      .json<CompanyInvoiceCheckDelay>();
  } catch (error) {
    log.error({
      message: 'Error fetching invoice check delay for company',
      error,
    });

    // Re-throw the error to propagate it to the calling function.
    throw error;
  }
};

/**
 * React Query based custom hook for getting the invoice check delay for a company with a given companyId.
 *
 * @param companyId - The ID of the company to fetch.
 * @param options - Additional options for the useQuery hook.
 * @returns The result of the useQuery hook.
 *
 * @see https://app.dev.vestigas.com/redoc#tag/VESTIGAS-Admin-Company/operation/get_invoice_check_delay_vadmin_company__company_id__invoice_check_delay_get
 */
export const useQueryCompanyInvoiceCheckDelay = (
  companyId: UUID,
  options?: Omit<
    UseQueryOptions<CompanyInvoiceCheckDelay>,
    'queryKey' | 'queryFn'
  >,
) => {
  return useQuery<CompanyInvoiceCheckDelay>({
    enabled: Boolean(companyId),
    queryFn: async () => fetchCompanyInvoiceCheckDelay(companyId),
    queryKey: queryKeysCompany.getInvoiceCheckDelay(companyId),
    ...options,
  });
};
